import { isNil } from 'es-toolkit';

import urlFrom from '@savvy/utils/error-handling/url-from';

import { apiHost } from '../../api/api-host';

const DUMMY_HOST = 'internal.test';
const DUMMY_BASE = `http://${DUMMY_HOST}`;

const apiHostResult = !isNil(apiHost) ? urlFrom(apiHost) : null;
const ALLOWED_HOSTS = [DUMMY_HOST, ...(apiHostResult?.isOk() ? [apiHostResult.value.host] : [])];

/**
 * Validates that a URL is safe for redirect (internal/relative paths only or allowed hosts).
 * Prevents open redirect vulnerabilities by rejecting external URLs.
 *
 * Safe URLs:
 * - Relative paths starting with "/" (e.g., "/dashboard", "/portfolio/123")
 * - Absolute URLs to allowed hosts (e.g., "https://api.savvywealth.com/...")
 *
 * Unsafe URLs (rejected):
 * - Absolute URLs to unknown hosts (e.g., "https://evil.com")
 * - Protocol-relative URLs (e.g., "//evil.com")
 * - URLs with embedded credentials or javascript (e.g., "javascript:alert(1)")
 */
export function isSafeRedirectUrl(url: string | undefined): url is string {
  if (isNil(url) || url.length === 0) {
    return false;
  }

  // Reject protocol-relative URLs (e.g., "//evil.com")
  if (url.startsWith('//')) {
    return false;
  }

  // Check if it's an absolute URL to an allowed host
  const absoluteResult = urlFrom(url);
  if (absoluteResult.isOk()) {
    return ALLOWED_HOSTS.includes(absoluteResult.value.host);
  }

  // For relative paths, must start with "/" to be safe
  if (!url.startsWith('/')) {
    return false;
  }

  const parseResult = urlFrom(url, DUMMY_BASE);
  return parseResult.isErr() ? false : ALLOWED_HOSTS.includes(parseResult.value.host);
}
